/**
 * SkillBuilderContext — the Skill Builder conversation, lifted out of the page.
 *
 * WHY THIS EXISTS. The builder used to be a dialog rendered inside
 * SkillsPage, which is a route element. Navigating anywhere unmounted it:
 * the thread, the poll timer and the "what you need to do next" checklist
 * all died, so every next-step action had to close the panel before it
 * could send you to the page where the work happens. That is backwards —
 * the checklist is most useful exactly while you are doing the thing it
 * describes.
 *
 * So the state lives here, in a provider mounted ABOVE <Routes> (see
 * AdminApp), and SkillBuilderRail is a dumb renderer of it. The panel now
 * survives navigation, and a build keeps polling while the merchant works
 * somewhere else in the admin.
 *
 * There is still no message store: one row of `skill_build_requests` is one
 * exchange (merchant text in `requirement`, reply in `result`) and a
 * conversation is the rows sharing a `thread_id`. See
 * ac-fastapi-staging-main/app/routers/skill_builder.py.
 *
 * Nothing here is a new endpoint. Same POST, same poll, same one-build-at-a-
 * time guard on the server — this is a move, not a feature.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  getSkillBuild,
  listSkillBuilds,
  listSkills,
  startSkillBuild,
} from '../api/skills';

const POLL_MS = 2500;
const THREAD_LIMIT = 50;
const STORAGE_KEY = 'aerochat_skill_builder_panel';

/** Shortest requirement we will send. Below this the model has nothing to go on. */
export const BUILD_MIN_CHARS = 20;

/** Rail widths. `full` covers the content area; the others push it. */
export const RAIL_MODES = ['docked', 'wide', 'full'];

/** Fired when a turn settles, so any mounted page can refresh its own data. */
export const SKILLS_CHANGED_EVENT = 'aerochat:skills-changed';

export const isPending = (turn) =>
  Boolean(turn) && (turn.status === 'queued' || turn.status === 'running');

/**
 * Announce that skills changed. `source` lets each listener skip the echo of
 * its own write — both sides already refresh themselves directly, so without
 * it every activation would cost two round trips.
 */
export const notifySkillsChanged = (source = 'rail') => {
  window.dispatchEvent(new CustomEvent(SKILLS_CHANGED_EVENT, { detail: { source } }));
};

const readStored = () => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    // Private mode, quota, corrupt value — the panel just opens closed.
    return {};
  }
};

const SkillBuilderContext = createContext(null);

export function useSkillBuilder() {
  const ctx = useContext(SkillBuilderContext);
  if (!ctx) {
    throw new Error('useSkillBuilder must be used inside <SkillBuilderProvider>');
  }
  return ctx;
}

export function SkillBuilderProvider({ children }) {
  const stored = useMemo(readStored, []);
  const [open, setOpen] = useState(() => stored.open === true);
  const [mode, setModeState] = useState(() =>
    RAIL_MODES.includes(stored.mode) ? stored.mode : 'docked'
  );

  const [threadId, setThreadId] = useState(null);
  const [turns, setTurns] = useState([]);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  // A turn finished while the panel was closed. Drives the dot on the
  // header launcher — without it a merchant who navigated away has no way
  // to learn the answer arrived.
  const [unread, setUnread] = useState(false);
  // Skills by name, so a checklist row can resolve its target to an id.
  const [skills, setSkills] = useState([]);

  // Whether we have already tried to resume the last conversation. Kept in a
  // ref so opening twice does not re-fetch, and so the fetch never runs for a
  // merchant who never opens the panel.
  const hydratedRef = useRef(false);
  const settledRef = useRef(null);

  const pending = useMemo(() => turns.find(isPending) || null, [turns]);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ open, mode }));
    } catch {
      // Persistence is a convenience; never let it break the panel.
    }
  }, [open, mode]);

  const refreshSkills = useCallback(async () => {
    try {
      const data = await listSkills();
      setSkills(Array.isArray(data) ? data : []);
    } catch {
      // Only used to resolve names to ids; a failure just hides those buttons.
    }
  }, []);

  /** Load the most recent conversation, once, on first open. */
  const hydrate = useCallback(async () => {
    if (hydratedRef.current) return;
    hydratedRef.current = true;
    setLoading(true);
    try {
      const recent = await listSkillBuilds(1);
      const last = Array.isArray(recent) && recent.length ? recent[0] : null;
      if (!last) return;
      if (!last.thread_id) {
        setTurns([last]);
        return;
      }
      setThreadId(last.thread_id);
      try {
        const rows = await listSkillBuilds(THREAD_LIMIT, last.thread_id);
        setTurns(Array.isArray(rows) && rows.length ? rows : [last]);
      } catch {
        // Fall back to the one turn we have rather than an empty panel that
        // looks like the conversation was lost.
        setTurns([last]);
      }
    } catch {
      // A 503 here means the builder is not configured on this server; the
      // composer still renders and says so on send.
    } finally {
      setLoading(false);
    }
  }, []);

  const openPanel = useCallback(() => {
    setOpen(true);
    setUnread(false);
  }, []);

  // Loading hangs off `open`, not off openPanel(), because the panel can also
  // come back open from localStorage on a reload — in which case nobody calls
  // openPanel and the conversation would render as an empty intro. hydrate()
  // is guarded by a ref, so this costs one fetch per session either way.
  useEffect(() => {
    if (!open) return;
    hydrate();
    refreshSkills();
  }, [open, hydrate, refreshSkills]);

  const closePanel = useCallback(() => setOpen(false), []);

  const togglePanel = useCallback(() => {
    if (open) {
      setOpen(false);
      return;
    }
    openPanel();
  }, [open, openPanel]);

  const setMode = useCallback((next) => {
    setModeState(RAIL_MODES.includes(next) ? next : 'docked');
  }, []);

  // Skills can also change from the Skills page itself. Pick those up, so a
  // checklist row does not keep offering Activate for a skill the merchant
  // just activated from the table.
  useEffect(() => {
    const onChanged = (e) => {
      if (e?.detail?.source === 'rail') return;
      refreshSkills();
    };
    window.addEventListener(SKILLS_CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(SKILLS_CHANGED_EVENT, onChanged);
  }, [refreshSkills]);

  // Poll the one turn still running. Deliberately NOT gated on `open`: the
  // whole point of lifting this out of the page is that a build survives
  // navigation, and that includes surviving the panel being collapsed.
  useEffect(() => {
    if (!pending) return undefined;
    let cancelled = false;
    const timer = setInterval(async () => {
      try {
        const next = await getSkillBuild(pending.id);
        if (cancelled || !next) return;
        setTurns((prev) => prev.map((x) => (x.id === next.id ? next : x)));
      } catch {
        // Keep polling; a transient error must not strand the thread.
      }
    }, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [pending]);

  // A settled turn may have written drafts. Refresh the name->id map and tell
  // the rest of the app, so a mounted SkillsPage shows the new rows.
  useEffect(() => {
    const settled = turns.filter((x) => x.status === 'done' || x.status === 'failed');
    if (!settled.length) return;
    const newest = settled[settled.length - 1];
    if (settledRef.current === newest.id) return;
    // First render after hydrating an old thread: adopt it silently rather
    // than announcing a result the merchant has already seen.
    const firstPass = settledRef.current === null;
    settledRef.current = newest.id;
    refreshSkills();
    if (firstPass) return;
    notifySkillsChanged();
    if (!open) setUnread(true);
  }, [turns, open, refreshSkills]);

  const send = useCallback(
    async (text, t) => {
      const requirement = (text || '').trim();
      // The minimum applies to the first message of a thread; a follow-up
      // ("try again") only has to be non-empty. Mirrors the server.
      const minChars = threadId ? 1 : BUILD_MIN_CHARS;
      if (requirement.length < minChars || sending || pending) return;
      setSending(true);
      setError(null);
      try {
        const row = await startSkillBuild({ requirement, thread_id: threadId });
        setThreadId(row.thread_id || threadId);
        setTurns((prev) => [...prev, row]);
        setDraft('');
      } catch (e) {
        const code = e?.detail?.code;
        if (e.status === 409 || code === 'build_in_progress') setError(t('builder.inProgress'));
        else if (e.status === 429 || code === 'daily_limit') setError(t('builder.limit'));
        else if (e.status === 503) setError(t('builder.notConfigured'));
        else setError(e.message);
      } finally {
        setSending(false);
      }
    },
    [pending, sending, threadId]
  );

  const startOver = useCallback(() => {
    setThreadId(null);
    setTurns([]);
    setDraft('');
    setError(null);
    settledRef.current = null;
  }, []);

  const value = useMemo(
    () => ({
      open,
      mode,
      setMode,
      openPanel,
      closePanel,
      togglePanel,
      unread,
      threadId,
      turns,
      pending,
      loading,
      sending,
      error,
      setError,
      draft,
      setDraft,
      send,
      startOver,
      skills,
      refreshSkills,
    }),
    [
      open, mode, setMode, openPanel, closePanel, togglePanel, unread,
      threadId, turns, pending, loading, sending, error, draft, send,
      startOver, skills, refreshSkills,
    ]
  );

  return (
    <SkillBuilderContext.Provider value={value}>{children}</SkillBuilderContext.Provider>
  );
}

export default SkillBuilderContext;
